import { whatsAppHref } from '@/lib/utils';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';

const SESSION_KEY = 'whatsapp-greeting-dismissed';

/*
 * The authentic WhatsApp glyph (Simple Icons' path data, CC0) rather than a
 * BrandIcons.tsx-style stroke recreation — this button's whole job is instant
 * recognition as *that specific channel*, which depends on the real filled
 * mark, not an outline reinterpretation.
 */
export const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.36.101 11.943c0 2.096.549 4.142 1.595 5.945L0 24l6.335-1.652a11.882 11.882 0 0 0 5.71 1.447h.005c6.582 0 11.94-5.36 11.943-11.943a11.86 11.86 0 0 0-3.473-8.403" />
  </svg>
);

/*
 * Persistent WhatsApp chat CTA. Replaces the old InquiryButton "Send enquiry"
 * pill at the same fixed corner — WhatsApp is the main lead channel here, so
 * unlike that button this one never hides: no scroll-gating, and it shows on
 * /contact too (InquiryButton used to hide there since the enquiry form was
 * already the whole page).
 *
 * `phone` comes from CategoryContext via the caller — pass nothing while it's
 * still null and this renders nothing, same guard AppLayout already uses for
 * its own phone link.
 */
const WhatsAppButton = ({ phone }: { phone: string }) => {
  const href = whatsAppHref(phone);
  const [showBubble, setShowBubble] = useState(false);

  useEffect(() => {
    let alreadyDismissed = false;
    try {
      alreadyDismissed = sessionStorage.getItem(SESSION_KEY) === '1';
    } catch {
      // Private browsing etc. - treat as not dismissed, just don't persist later.
    }

    if (alreadyDismissed) {
      return;
    }

    const timer = setTimeout(() => setShowBubble(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  const dismissBubble = () => {
    setShowBubble(false);
    try {
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch {
      // Ignore - worst case the bubble reappears on the next page this session.
    }
  };

  return (
    <div className="fixed bottom-5 right-5 z-40 flex flex-col items-end gap-3">
      {showBubble && (
        <div className="animate-in fade-in-0 slide-in-from-bottom-2 flex max-w-64 items-start gap-2 rounded-2xl bg-card p-3 text-card-foreground shadow-pop">
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              window.dataLayer?.push({ event: 'whatsapp_click' });
              dismissBubble();
            }}
            className="text-sm"
          >
            <span className="mr-1 align-[-3px] text-xl" aria-hidden>
              👋
            </span>
            Chat with us on WhatsApp
          </a>
          <button
            type="button"
            onClick={dismissBubble}
            aria-label="Dismiss"
            className="shrink-0 text-muted-foreground transition-colors hover:text-foreground"
          >
            <X size={14} />
          </button>
        </div>
      )}

      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => window.dataLayer?.push({ event: 'whatsapp_click' })}
        aria-label="Chat on WhatsApp"
        className="relative inline-flex size-14 items-center justify-center rounded-full bg-whatsapp text-whatsapp-foreground shadow-pop transition-transform hover:scale-105"
      >
        {!showBubble && (
          <span
            aria-hidden
            className="absolute inset-0 animate-ping rounded-full bg-whatsapp opacity-40"
          />
        )}
        <WhatsAppIcon className="relative size-7" />
      </a>
    </div>
  );
};

export default WhatsAppButton;
